import { ReactNode, useEffect, useMemo, useState } from "react"

import { ArrowLeft, ArrowRight, Info, Save } from "lucide-react"
import { toast } from "sonner"

import { UploadDocumentsButton } from "@/components/buttons/UploadDocumentsButton"
import { UploadTemplateButton } from "@/components/buttons/UploadTemplateButton"
import { AdditionalDocumentsSelectionPanel } from "@/features/report/AdditionalDocumentsSelectionPanel"
import { ReportDocumentSelection } from "@/features/report/ReportDocumentSelection"
import { DocumentsSelectionPanel } from "@/features/report/DocumentsSelectionPanel"
import { ReportAdditionalDetailsPanel } from "@/features/report/ReportAdditionalDetailsPanel"
import { ReportAdditionalDocumentSelection } from "@/features/report/ReportAdditionalDocumentSelection"
import { ReportConfigurationDonePanel } from "@/features/report/ReportConfigurationDonePanel"
import { ReportCreationHeaderPanel } from "@/features/report/ReportCreationHeaderPanel"
import { ReportDetailsPanel } from "@/features/report/ReportDetailsPanel"
import { ReportPanelHeaderPanel } from "@/features/report/ReportPanelHeaderPanel"
import { ReportCreationSummaryPanel } from "@/features/report/ReportCreationSummaryPanel"
import { ReportTypeSelectionPanel } from "@/features/report/ReportTypeSelectionPanel"
import { isAdmin } from "@/lib/users"
import { Button } from "@ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/tooltip"
import { useUserSession } from "@session/UserSession"

import { AsyncConversationExecutionPayload, CollectionItem, ContentObjectItem, Interaction } from "@vertesia/common"

const env = import.meta.env

const PROGRAM_TYPE_ID = "68219c5af3aed394f1ec4969"
const GENERATE_INTERACTION_ID = env.VITE_GENERATE_REPORT_INTERACTION_ID || "681dd5b6ce05538a764c18ab"
const GENERATE_INTERACTION_HIGH_VOLUME_ID = env.VITE_GENERATE_REPORT_INTERACTION_HIGH_VOLUME_ID || "6936951844028a2ca0f5c1ef"
const TEMPLATE_CONFIGURATION_ID = "68a41526a394c063d9bc6f73"
const TEMPLATE_ID = "687ea5acaecd239e450bc9f6"
const DOCUMENT_SELECTION_SOFT_LIMIT = 40
const DOCUMENT_SELECTION_HARD_LIMIT = 40

interface CreateReportGeneratorPanelProps {
    collection?: CollectionItem | undefined
    initialCollection?: CollectionItem | undefined
    children: ReactNode
}
export function CreateReportGeneratorPanel({ children, collection, initialCollection }: Readonly<CreateReportGeneratorPanelProps>) {
    const { client, isLoading, store, user } = useUserSession()
    const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false)

    const [lowVolumeInteraction, setLowVolumeInteraction] = useState<Interaction | undefined>()
    const [highVolumeInteraction, setHighVolumeInteraction] = useState<Interaction | undefined>()

    const [reportState, setReportState] = useState<"details" | "type" | "documents" | "additional" | "additional_details" | "summary" | "done">("details")
    const [loading, setLoading] = useState(false)

    const [configurations, setConfigurations] = useState<CollectionItem[]>([])
    const [templates, setTemplates] = useState<ContentObjectItem[]>([])

    const [collectionToUpdate, setCollectionToUpdate] = useState<CollectionItem | undefined>(undefined)
    const [reportTitle, setReportTitle] = useState("")
    const [reportDescription, setReportDescription] = useState("")
    const [selectedDocuments, setSelectedDocuments] = useState<ContentObjectItem[]>([])
    const [additionalDocuments, setAdditionalDocuments] = useState<ContentObjectItem[]>([])
    const [configuration, setConfiguration] = useState<CollectionItem | undefined>(undefined)
    const [template, setTemplate] = useState<ContentObjectItem | undefined>(undefined)
    const [additional, setAdditional] = useState<Record<string, string>>({})

    const documentsTokenIssue = useMemo(() => {
        if (!selectedDocuments.length) {
            return 0
        }

        return selectedDocuments
            .map((doc) => doc?.tokens?.count ?? 0)
            .filter((token) => token >= 1000000)
            .length
    }, [selectedDocuments])

    const additionalTokenIssue = useMemo(() => {
        if (!additionalDocuments.length) {
            return 0
        }

        return additionalDocuments
            .map((doc) => doc?.tokens?.count ?? 0)
            .filter((token) => token >= 1000000)
            .length
    }, [additionalDocuments])

    const totalDocumentCount = useMemo(() => {
        return selectedDocuments.length + additionalDocuments.length
    }, [selectedDocuments, additionalDocuments])

    const isDocumentCountExceeded = useMemo(() => {
        return totalDocumentCount > DOCUMENT_SELECTION_HARD_LIMIT
    }, [totalDocumentCount])

    const handleTemplateConfigurationUpdate = (configuration: CollectionItem | undefined) => {
        setConfiguration(configuration)
        setTemplates([])
        setTemplate(undefined)
        setAdditional({})

        searchTemplates(configuration)
    }

    const handleSaveModifications = async () => {
        if (!store || !reportTitle || !reportDescription) {
            return
        }

        if (!collectionToUpdate) {
            const response = await createCollection()
            if (!response) {
                toast.error("Failed to create Report Configuration", {
                    description: "Please try again later.",
                    duration: 5000
                })
                return
            }

            toast.success("Report Configuration successfully created", { duration: 3000 })
            addDocumentsToCollection(response.id)
            setCollectionToUpdate(response)
        } else {
            const response = await updateCollection(collectionToUpdate.id, collection?.id || (collectionToUpdate as any)?.properties?.parent || undefined, "configuration", undefined, undefined)
            if (!response) {
                toast.error("Failed to update Report Configuration", {
                    description: "Please try again later.",
                    duration: 5000
                })
                return
            }

            toast.success("Report Configuration successfully updated", { duration: 3000 })
            const documentsIds = (collectionToUpdate as any)?.properties?.documents || []
            const additionalIds = (collectionToUpdate as any)?.properties?.additional_documents || []
            const ids = [...documentsIds, ...additionalIds]
            if (ids.length) {
                removeDocumentsFromCollection(response.id, ids)
            }
            addDocumentsToCollection(response.id)
        }
    }

    const handleContinue = () => {
        if (reportState === "details") {
            setReportState("type")
        } else if (reportState === "type") {
            setReportState("documents")
        } else if (reportState === "documents") {
            setReportState("additional")
        } else if (reportState === "additional") {
            setReportState("additional_details")
        } else if (reportState === "additional_details") {
            setReportState("summary")
        } else if (reportState === "summary") {
            setReportState("done")
            generateReport()
        }
    }

    const handleBack = () => {
        if (reportState === "summary") {
            setReportState("additional_details")
        } else if (reportState === "additional_details") {
            setReportState("additional")
        } else if (reportState === "additional") {
            setReportState("documents")
        } else if (reportState === "documents") {
            setReportState("type")
        } else if (reportState === "type") {
            setReportState("details")
        }
    }

    const handleOnUploadDoneAdditionalDocuments = (documents: ContentObjectItem[]) => {
        if (!documents || documents.length === 0) {
            return
        }
        const newDocuments = documents.filter(doc => !additionalDocuments.some(selected => selected.id === doc.id))
        setAdditionalDocuments(prev => [...prev, ...newDocuments])
    }

    const searchTemplateConfigurations = (initialConfiguration: string | undefined) => {
        if (!client || isLoading) {
            return
        }

        setLoading(true)
        client.store.collections.search({ type: TEMPLATE_CONFIGURATION_ID })
            .then((response) => {
                setConfigurations(response.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()))
                if (initialConfiguration) {
                    const foundConfiguration = response.find(config => config.id === initialConfiguration)
                    setConfiguration(foundConfiguration || undefined)
                }
            })
            .catch((error) => {
                toast.error("Failed to load template configurations", {
                    description: error.message,
                    duration: 4000
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const searchTemplates = (configuration: CollectionItem | undefined, templateId?: string) => {
        if (!client || isLoading || !configuration) {
            return
        }

        setLoading(true)
        client.store.collections.searchMembers(configuration.id, { query: { type: TEMPLATE_ID } })
            .then((response) => {
                setTemplates(response.results)
                if (templateId) {
                    const foundTemplate = response.results.find(template => template.id == templateId)
                    setTemplate(foundTemplate || undefined)
                } else {
                    setTemplate(undefined)
                }
            })
            .catch((error) => {
                toast.error("Failed to load templates", {
                    description: error.message,
                    duration: 4000
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const createCollection = async () => {
        if (!store) {
            return null
        }

        const properties = {
            status: "configuration",
            documents: selectedDocuments.map(doc => doc.id),
            additional_documents: additionalDocuments.map(doc => doc.id),
            configuration: configuration?.id || "",
            template: template?.id || "",
            additional,
            parent: collection?.id || undefined
        }

        const payload = {
            name: reportTitle,
            description: reportDescription,
            dynamic: false,
            type: PROGRAM_TYPE_ID,
            properties
        }

        return await store.collections.create(payload)
    }

    const updateCollection = async (collectionId: string, parentId: string | undefined, status: string, run_id: string | undefined, workflow_id: string | undefined) => {
        if (!store || !collectionId) {
            return
        }

        const properties = {
            status,
            documents: selectedDocuments.map(doc => doc.id),
            additional_documents: additionalDocuments.map(doc => doc.id),
            configuration: configuration?.id || "",
            template: template?.id || "",
            additional,
            parent: parentId,
            run_id,
            workflow_id
        }

        const payload = {
            name: reportTitle,
            description: reportDescription,
            dynamic: false,
            type: PROGRAM_TYPE_ID,
            properties
        }

        return await store.collections.update(collectionId, payload)
    }

    const addDocumentsToCollection = async (collectionId: string) => {
        const documentIds = selectedDocuments.map(doc => doc.id)
        const additionalIds = additionalDocuments.map(doc => doc.id)
        const ids = [...documentIds, ...additionalIds]
        if (!ids.length) {
            return
        }
        await store.collections.addMembers(collectionId, ids)
    }

    const removeDocumentsFromCollection = async (collectionId: string, ids: string[]) => {
        await store.collections.deleteMembers(collectionId, ids)
    }

    const generateReport = async () => {
        const interaction = totalDocumentCount <= DOCUMENT_SELECTION_SOFT_LIMIT ? lowVolumeInteraction : highVolumeInteraction
        if (!template || !interaction || !client) {
            return
        }

        const report = collectionToUpdate ? collectionToUpdate : (await createCollection())
        const reportId = report?.id
        if (!reportId) {
            toast.error("Failed to create report collection", {
                description: "Please try again later.",
                duration: 5000
            })
            return
        }

        await addDocumentsToCollection(reportId)

        const payload = {
            collection_id: reportId,
            type: "conversation",
            data: {
                reports: generateReportsList(selectedDocuments, additionalDocuments),
                style_guideline: `store:${template.id}`,
                collection: reportId,
                additional
            },
            interactive: true,
            interaction: interaction.endpoint,
            environment: interaction.environment,
            model: interaction.model,
            tools: [],
            visibility: "project",
            tags: [`program:${reportId}`],
        } as AsyncConversationExecutionPayload

        client.interactions.executeAsync(payload)
            .then(async (run) => {
                await updateCollection(reportId, collection?.id || (report as any)?.properties?.parent || undefined, "processing", run.runId, run.workflowId)
                window.location.pathname = `/report-generator/${reportId}`
            }).catch((err) => {
                toast.error("Failed to start Report Generation", {
                    description: err.message,
                    duration: 5000
                })
            })
    }

    const searchGenerateReportInteraction = () => {
        if (!client || isLoading) {
            return
        }

        client.interactions.retrieve(GENERATE_INTERACTION_ID)
            .then((response) => {
                setLowVolumeInteraction(response as Interaction)
            })
            .catch((error) => {
                toast.error("Failed to load Generate Report Interaction", {
                    description: error.message,
                    duration: 4000
                })
            })

        client.interactions.retrieve(GENERATE_INTERACTION_HIGH_VOLUME_ID)
            .then((response) => {
                setHighVolumeInteraction(response as Interaction)
            })
            .catch((error) => {
                toast.error("Failed to load Generate Report High Volume Interaction", {
                    description: error.message,
                    duration: 4000
                })
            })
    }

    const loadCollectionProperties = (collectionId: string, saveCollection: boolean) => {
        store.collections.retrieve(collectionId)
            .then((response) => {
                if (saveCollection) {
                    setCollectionToUpdate(response)
                }

                setReportTitle(response.name || "")
                setReportDescription(response.description || "")

                const properties = (response as any)?.properties || {}

                const tmpDocuments: string[] = properties?.documents || []
                if (tmpDocuments.length) {
                    const uploadPromises = tmpDocuments.map(doc => client.store.objects.retrieve(doc))
                    Promise.all(uploadPromises).then((inputs) => {
                        setSelectedDocuments(inputs)
                    })
                }

                const tmpAdditionalDocuments: string[] = properties?.additional_documents || []
                if (tmpAdditionalDocuments.length) {
                    const uploadPromises = tmpAdditionalDocuments.map(doc => client.store.objects.retrieve(doc))
                    Promise.all(uploadPromises).then((inputs) => {
                        setAdditionalDocuments(inputs)
                    })
                }

                const configurationId = properties?.configuration
                const templateId = properties?.template
                if (configurationId) {
                    client.store.collections.retrieve(configurationId)
                        .then((configurationData) => {
                            setConfiguration(configurationData)
                            searchTemplates(configurationData, templateId)
                        })
                        .catch(() => {
                            setConfiguration(undefined)
                        })
                }

                if (templateId) {
                    client.store.objects.retrieve(templateId)
                        .then((templateData) => {
                            setTemplate(templateData)
                        })
                        .catch(() => {
                            setTemplate(undefined)
                        })
                }

                setAdditional(properties?.additional || {})
            })
    }

    const renderDocumentCountDisplay = () => {
        const isError = isDocumentCountExceeded
        const textColor = isError ? "text-red-500" : "text-muted-foreground"

        return (
            <div className="flex items-center gap-1 text-xl font-medium">
                <span className={textColor}>
                    {totalDocumentCount}/{DOCUMENT_SELECTION_HARD_LIMIT}
                </span>
                {
                    (isError) && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 text-red-500 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-base max-w-md">
                                        Report listing is over {DOCUMENT_SELECTION_HARD_LIMIT}, please remove documents to ensure total documents included in this report is {DOCUMENT_SELECTION_HARD_LIMIT} or less.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )
                }
            </div>
        )
    }

    const handleDocumentSelection = (documents: ContentObjectItem[]) => {
        const selectedIds = new Set(selectedDocuments.map(doc => doc.id))
        const newDocuments = documents.filter(doc => !selectedIds.has(doc.id))
        setSelectedDocuments(prev => [...prev, ...newDocuments])
    }

    const handleDocumentsRemoved = (documents: ContentObjectItem[]) => {
        setSelectedDocuments(documents)
    }

    const handleAdditionalDocumentSelection = (documents: ContentObjectItem[]) => {
        const selectedIds = new Set(selectedDocuments.map(doc => doc.id))
        const additionalIds = new Set(additionalDocuments.map(doc => doc.id))
        const newDocuments = documents.filter(doc => !selectedIds.has(doc.id) && !additionalIds.has(doc.id))
        setAdditionalDocuments(prev => [...prev, ...newDocuments])
    }

    const handleAdditionalDocumentsRemoved = (documents: ContentObjectItem[]) => {
        setAdditionalDocuments(documents)
    }

    useEffect(() => {
        searchTemplateConfigurations(configuration?.id)
        searchGenerateReportInteraction()
    }, [client, isLoading])

    useEffect(() => {
        if (!collection) {
            return
        }

        loadCollectionProperties(collection.id, false)
    }, [collection])

    useEffect(() => {
        if (!initialCollection) {
            return
        }

        loadCollectionProperties(initialCollection.id, true)
    }, [initialCollection])

    useEffect(() => {
        setIsUserAdmin(user ? isAdmin(user) : false)
    }, [user])

    return (
        <div className="flex flex-col w-full h-full bg-gray-50 p-4 gap-4">
            <div className="flex flex-row w-full justify-between text-center flex-shrink-0">
                <div className="w-1/4 items-start flex">
                    {children}
                </div>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                        {
                            collection
                                ? "Copy Existing Report"
                                : initialCollection
                                    ? "Update Report"
                                    : "Create New Report"
                        }
                    </h1>
                    <p className="text-gray-600">
                        {
                            collection
                                ? "Copy an existing report to modify and generate a new version"
                                : "Generating content for funder/partner reports."
                        }
                    </p>
                </div>
                <div className="w-1/4 items-start justify-end flex">
                    <Button onClick={handleSaveModifications}>
                        <Save />
                        Save Modifications
                    </Button>
                </div>
            </div>
            <div className="flex flex-col items-center w-full flex-1 min-h-0 gap-4">
                <div className="flex gap-4 flex-col items-center flex-shrink-0 w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-3xl xl:max-w-4xl">
                    <ReportCreationHeaderPanel reportState={reportState} />
                </div>
                <div className="flex-1 flex flex-col min-h-0 w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-3xl xl:max-w-4xl">
                    <div className="rounded-lg border bg-card text-card-foreground flex flex-col h-full">
                        <ReportPanelHeaderPanel state={reportState}>
                            {
                                (reportState === "documents" && selectedDocuments.length > 0) && (
                                    <ReportDocumentSelection configuration={configuration!} handleDocumentSelection={handleDocumentSelection} more />
                                )
                            }
                            {
                                (reportState === "additional" && additionalDocuments.length > 0) && (
                                    <ReportAdditionalDocumentSelection handleDocumentSelection={handleAdditionalDocumentSelection} more />
                                )
                            }
                        </ReportPanelHeaderPanel>
                        <div className="p-3 pt-0 flex-1 flex flex-col min-h-0 gap-3">
                            <div className="flex-1 flex flex-col space-y-3 min-h-0">
                                {
                                    (reportState === "details") &&
                                    <ReportDetailsPanel collection={collection} title={reportTitle} description={reportDescription} setTitle={setReportTitle} setDescription={setReportDescription} />
                                }
                                {
                                    (reportState === "type") &&
                                    <ReportTypeSelectionPanel configuration={configuration} configurations={configurations} template={template} templates={templates} loading={loading} handleTemplateDelete={() => searchTemplates(configuration)} handleTemplateUpdate={setTemplate} handleTemplateConfigurationUpdate={handleTemplateConfigurationUpdate} />
                                }
                                {
                                    (reportState === "documents") &&
                                    <DocumentsSelectionPanel configuration={configuration!} documents={selectedDocuments} handleDocumentSelection={handleDocumentSelection} handleDocumentsRemoved={handleDocumentsRemoved} />
                                }
                                {
                                    (reportState === "additional") &&
                                    <AdditionalDocumentsSelectionPanel documents={additionalDocuments} handleDocumentSelection={handleAdditionalDocumentSelection} handleDocumentsRemoved={handleAdditionalDocumentsRemoved} />
                                }
                                {
                                    (reportState === "additional_details") &&
                                    <ReportAdditionalDetailsPanel configuration={configuration!} additional={additional} setAdditional={setAdditional} />
                                }
                                {
                                    (reportState === "summary") &&
                                    <ReportCreationSummaryPanel title={reportTitle} setTitle={setReportTitle} description={reportDescription} setDescription={setReportDescription} configuration={configuration!} template={template!} documents={selectedDocuments} additional={additionalDocuments} details={additional} setDetails={setAdditional} onNavigate={setReportState} />
                                }
                                {
                                    (reportState === "done") &&
                                    <ReportConfigurationDonePanel />
                                }
                            </div>
                            {
                                reportState !== "done" &&
                                <div className="flex justify-between flex-shrink-0">
                                    <div>
                                        {
                                            reportState !== "details" && (
                                                <Button variant="outline" onClick={handleBack}>
                                                    <ArrowLeft />
                                                    Back
                                                </Button>
                                            )
                                        }
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {
                                            (reportState === "documents" || reportState === "additional") && (
                                                renderDocumentCountDisplay()
                                            )
                                        }
                                        {
                                            (reportState === "type" && isUserAdmin && !!configuration) && (
                                                <UploadTemplateButton configurationId={configuration?.id} onUploadDone={(documentId) => searchTemplates(configuration, documentId)} />
                                            )
                                        }
                                        {
                                            (reportState === "additional") && (
                                                <UploadDocumentsButton onUploadDone={handleOnUploadDoneAdditionalDocuments} light skip />
                                            )
                                        }
                                        <Button
                                            onClick={handleContinue}
                                            disabled={
                                                (reportState === "details" && (!reportTitle || !reportDescription || (collection?.name && reportTitle.trim() === collection.name.trim())))
                                                || (reportState === "type" && (!configuration || !template))
                                                || (reportState === "documents" && (!selectedDocuments.length || documentsTokenIssue !== 0 || isDocumentCountExceeded))
                                                || (reportState === "additional" && (additionalTokenIssue !== 0 || isDocumentCountExceeded))
                                                || (reportState === "additional_details" && (configuration as any)?.additional?.some((field: any) => field.required && !additional[field.name]))
                                                || (reportState === "summary" && (!reportTitle || !reportDescription || (collection?.name && reportTitle.trim() === collection.name.trim()) || !configuration || !template || (configuration as any)?.additional?.some((field: any) => field.required && !additional[field.name]) || isDocumentCountExceeded))
                                            }
                                        >
                                            {
                                                reportState !== "summary" && reportState !== "additional_details" ? (
                                                    "Continue"
                                                ) : (
                                                    reportState === "summary" ? (
                                                        "Generate Report"
                                                    ) : (
                                                        "Summary"
                                                    )
                                                )
                                            }
                                            <ArrowRight />
                                        </Button>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function generateReportsList(documents: ContentObjectItem[], additionalDocuments: ContentObjectItem[]) {
    return [
        ...documents.map(doc => `store:${doc.id}`),
        ...additionalDocuments.map(doc => `store:${doc.id}`)
    ]
}